export const parseLocalDate = (value) => {
  if (value instanceof Date) return new Date(value);
  const [year, month, day] = String(value || '').slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const startOfDay = (value) => {
  const date = parseLocalDate(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const toDateKey = (value) => {
  const date = parseLocalDate(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const addDays = (value, count) => {
  const date = parseLocalDate(value);
  date.setDate(date.getDate() + count);
  return date;
};

export const addWeeks = (value, count) => addDays(value, count * 7);

export const addMonths = (value, count) => {
  const source = parseLocalDate(value);
  const sourceDay = source.getDate();
  const target = new Date(source);
  target.setDate(1);
  target.setMonth(target.getMonth() + count);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(sourceDay, lastDay));
  return target;
};

export const startOfWeek = (value) => {
  const date = startOfDay(value);
  date.setDate(date.getDate() + (date.getDay() === 0 ? -6 : 1 - date.getDay()));
  return date;
};

export const endOfWeek = (value) => addDays(startOfWeek(value), 6);
export const startOfMonth = (value) => new Date(parseLocalDate(value).getFullYear(), parseLocalDate(value).getMonth(), 1);
export const endOfMonth = (value) => new Date(parseLocalDate(value).getFullYear(), parseLocalDate(value).getMonth() + 1, 0);
export const monthGridStart = (value) => startOfWeek(startOfMonth(value));
export const monthGridEnd = (value) => addDays(monthGridStart(value), 41);

export const minutesFromTime = (value = '00:00') => {
  const [hours, minutes] = String(value).split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

export const durationLabel = (minutes = 0) => `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`;

export const formatPeriod = (view, anchor) => {
  if (view === 'day') return anchor.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  if (view === 'month') return anchor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return `${startOfWeek(anchor).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${endOfWeek(anchor).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

export const getVisibleRange = (view, anchor) => {
  if (view === 'day') return { start: toDateKey(anchor), end: toDateKey(anchor) };
  if (view === 'month') return { start: toDateKey(monthGridStart(anchor)), end: toDateKey(monthGridEnd(anchor)) };
  return { start: toDateKey(startOfWeek(anchor)), end: toDateKey(endOfWeek(anchor)) };
};

export const navigatePlanningPeriod = (view, anchor, direction) => {
  if (view === 'day') return addDays(anchor, direction);
  if (view === 'month') return addMonths(anchor, direction);
  return addWeeks(anchor, direction);
};

export const eventInterval = (event) => ({
  start: new Date(`${event.startDate}T${event.startTime || '00:00'}:00`),
  end: new Date(`${event.endDate || event.startDate}T${event.endTime || event.startTime || '23:59'}:00`),
});

export const overlaps = (firstEvent, secondEvent) => {
  const first = eventInterval(firstEvent);
  const second = eventInterval(secondEvent);
  return first.start < second.end && second.start < first.end;
};

const dateTimeFromEvent = (event, boundary) => {
  const date = boundary === 'start' ? event.startDate : event.endDate;
  const time = boundary === 'start' ? event.startTime : event.endTime;
  if (!date || (!event.isAllDay && !time)) return null;
  return new Date(`${date}T${event.isAllDay ? '00:00' : time}:00`);
};

export const calculateDurationMinutes = (event) => {
  const start = dateTimeFromEvent(event, 'start');
  const end = dateTimeFromEvent(event, 'end');
  if (!start || !end) return Number.NaN;
  const difference = Math.round((end.getTime() - start.getTime()) / 60000);
  return event.isAllDay ? difference + 1440 : difference;
};

export const calculateEndFromDuration = (event) => {
  if (!event.startDate) return event;
  const durationMinutes = Math.max(0, Number(event.durationMinutes) || 0);
  if (event.isAllDay) {
    const dayCount = Math.max(1, Math.ceil(durationMinutes / 1440));
    return { ...event, endDate: toDateKey(addDays(event.startDate, dayCount - 1)), startTime: '', endTime: '' };
  }
  if (!event.startTime) return event;
  const end = new Date(`${event.startDate}T${event.startTime}:00`);
  end.setMinutes(end.getMinutes() + durationMinutes);
  return {
    ...event,
    endDate: toDateKey(end),
    endTime: `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`,
  };
};

export const normalizePlanningInterval = (event) => {
  const durationMinutes = calculateDurationMinutes(event);
  if (Number.isFinite(durationMinutes) && durationMinutes >= 0) return { ...event, durationMinutes };
  return calculateEndFromDuration({ ...event, durationMinutes: Math.max(0, Number(event.durationMinutes) || 0) });
};

export const updatePlanningIntervalField = (event, field, value) => {
  const next = { ...event, [field]: value };
  if (field === 'durationMinutes') return calculateEndFromDuration(next);
  if (field === 'isAllDay' && value) {
    return normalizePlanningInterval({ ...next, startTime: '', endTime: '' });
  }
  if (field === 'isAllDay' && !value) {
    return calculateEndFromDuration({ ...next, startTime: event.startTime || '09:00' });
  }
  if (['startDate', 'startTime', 'endDate', 'endTime', 'isAllDay'].includes(field)) {
    const durationMinutes = calculateDurationMinutes(next);
    return { ...next, durationMinutes: Number.isFinite(durationMinutes) ? durationMinutes : next.durationMinutes };
  }
  return next;
};
